/**
 * Tournament Schedule Generator
 * Supports: Single Elimination, Double Elimination, Round Robin, League + Finals, IPL-style
 */

// Utility: Shuffle array (Fisher-Yates)
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Utility: Get next power of 2
const nextPowerOf2 = (n) => {
  let power = 1;
  while (power < n) power *= 2;
  return power;
};

// Utility: Generate unique match ID
const generateMatchId = () => `match-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Utility: Get round name for elimination tournaments
const getEliminationRoundName = (roundNumber, totalRounds, isDoubleElim = false, isLosersBracket = false) => {
  const roundsFromFinal = totalRounds - roundNumber;

  if (isLosersBracket) {
    return `Losers Round ${roundNumber}`;
  }

  switch (roundsFromFinal) {
    case 0: return isDoubleElim ? 'Grand Final' : 'Final';
    case 1: return 'Semi-Finals';
    case 2: return 'Quarter-Finals';
    case 3: return 'Round of 16';
    case 4: return 'Round of 32';
    default: return `Round ${roundNumber}`;
  }
};

/**
 * SINGLE ELIMINATION GENERATOR
 * Creates a knockout bracket with byes for non-power-of-2 teams
 */
export const generateSingleElimination = (participants, options = {}) => {
  const { seeded = false, seedOrder = null, thirdPlace = false, consolation = 'none' } = options;

  // Shuffle or use provided seed order
  let teams = seeded && seedOrder ? seedOrder : shuffleArray([...participants]);

  const bracketSize = nextPowerOf2(teams.length);
  const byesNeeded = bracketSize - teams.length;
  const totalRounds = Math.log2(bracketSize);

  const rounds = [];
  const matches = [];

  // Create first round with byes
  const firstRoundMatches = [];
  let matchNumber = 1;

  // Distribute byes - top seeds get byes
  const teamsWithByes = [...teams];
  for (let i = 0; i < byesNeeded; i++) {
    teamsWithByes.splice(i * 2 + 1, 0, { id: `bye-${i}`, name: 'BYE', isBye: true });
  }

  // Create first round matches
  for (let i = 0; i < teamsWithByes.length; i += 2) {
    const teamA = teamsWithByes[i];
    const teamB = teamsWithByes[i + 1];

    const match = {
      id: generateMatchId(),
      matchNumber: matchNumber++,
      roundNumber: 1,
      roundName: getEliminationRoundName(1, totalRounds),
      teamA: teamA,
      teamB: teamB,
      isBye: teamA.isBye || teamB.isBye,
      winner: teamA.isBye ? teamB : (teamB.isBye ? teamA : null),
      status: teamA.isBye || teamB.isBye ? 'completed' : 'pending',
      scheduledAt: null,
      venue: null,
    };

    firstRoundMatches.push(match);
    matches.push(match);
  }

  rounds.push({
    roundNumber: 1,
    roundName: getEliminationRoundName(1, totalRounds),
    roundType: 'elimination',
    matches: firstRoundMatches,
  });

  // Create subsequent rounds (placeholders)
  let previousRoundMatches = firstRoundMatches;
  for (let round = 2; round <= totalRounds; round++) {
    const roundMatches = [];
    const matchesInRound = previousRoundMatches.length / 2;

    for (let i = 0; i < matchesInRound; i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: round,
        roundName: getEliminationRoundName(round, totalRounds),
        teamA: null, // TBD - winner of previous match
        teamB: null, // TBD - winner of previous match
        previousMatchA: previousRoundMatches[i * 2]?.id,
        previousMatchB: previousRoundMatches[i * 2 + 1]?.id,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };

      roundMatches.push(match);
      matches.push(match);
    }

    rounds.push({
      roundNumber: round,
      roundName: getEliminationRoundName(round, totalRounds),
      roundType: 'elimination',
      matches: roundMatches,
    });

    previousRoundMatches = roundMatches;
  }

  // Draft-only round numbering for third place / consolation, distinct from
  // the main bracket's 1..totalRounds: the real round_number is authoritative
  // only once the backend generator (KYN-234) persists these rounds.
  let nextDraftRoundNumber = totalRounds + 1;

  // Round-1 matches already claimed as a loser source by the third-place
  // block below, so the consolation block (which may draw from the same
  // round when totalRounds === 2) never double-books a loser to both a
  // third-place slot and a consolation slot. Mirrors the backend's
  // thirdPlaceClaimedRoundIds (scheduleGenerator.js, KYN-234).
  const thirdPlaceClaimedMatchIds = new Set();

  // Third place playoff — only possible when there's an actual semifinal
  // round (round before the final) to draw the two losers from. teams.length
  // >= 4 is required, not just totalRounds >= 2: at n=3 (bracketSize=4,
  // totalRounds=2) the semifinal round has a bye game, so only one of the
  // two semis has a real loser — the third-place slot can never be filled.
  // Mirrors the backend's KYN-459 guard exactly (scheduleGenerator.js).
  if (thirdPlace && totalRounds >= 2 && teams.length >= 4) {
    const semifinalRound = rounds[rounds.length - 2];
    const [semifinalMatchA, semifinalMatchB] = semifinalRound.matches;

    const thirdPlaceRoundNumber = nextDraftRoundNumber++;
    const thirdPlaceMatch = {
      id: generateMatchId(),
      matchNumber: matchNumber++,
      roundNumber: thirdPlaceRoundNumber,
      roundName: 'Third Place Playoff',
      roundType: 'third_place',
      teamA: null,
      teamB: null,
      // Parallel to previousMatchA/B, but sourced from the loser of each
      // semifinal rather than the winner, so downstream code can tell the
      // two slot kinds apart unambiguously. A bye semifinal has no loser,
      // so its slot is left unwired rather than seeding a phantom BYE.
      loserOfMatchA: semifinalMatchA.isBye ? null : semifinalMatchA.id,
      loserOfMatchB: semifinalMatchB.isBye ? null : semifinalMatchB.id,
      winner: null,
      status: 'pending',
      scheduledAt: null,
      venue: null,
    };

    if (!semifinalMatchA.isBye) thirdPlaceClaimedMatchIds.add(semifinalMatchA.id);
    if (!semifinalMatchB.isBye) thirdPlaceClaimedMatchIds.add(semifinalMatchB.id);

    matches.push(thirdPlaceMatch);
    rounds.push({
      roundNumber: thirdPlaceRoundNumber,
      roundName: 'Third Place Playoff',
      roundType: 'third_place',
      matches: [thirdPlaceMatch],
    });
  }

  // Consolation bracket — a parallel single-elim mini-bracket seeded from the
  // losers of round 1, excluding byes (a bye produces no loser to seed) and
  // excluding any round-1 loser already claimed by the third-place block
  // above (a match only has one loser, it can't feed two destinations).
  if (consolation === 'first_round') {
    const realFirstRoundMatches = firstRoundMatches.filter(
      (m) => !m.isBye && !thirdPlaceClaimedMatchIds.has(m.id)
    );

    if (realFirstRoundMatches.length >= 2) {
      const consolationEntrants = realFirstRoundMatches.map((m) => ({
        sourceMatchId: m.id,
        isBye: false,
      }));

      const consolationBracketSize = nextPowerOf2(consolationEntrants.length);
      const consolationByesNeeded = consolationBracketSize - consolationEntrants.length;
      const consolationTotalRounds = Math.log2(consolationBracketSize);

      const entrantsWithByes = [...consolationEntrants];
      for (let i = 0; i < consolationByesNeeded; i++) {
        entrantsWithByes.splice(i * 2 + 1, 0, { sourceMatchId: null, isBye: true });
      }

      const consolationRound1RoundNumber = nextDraftRoundNumber++;
      const consolationRound1Name = consolationTotalRounds === 1 ? 'Consolation Final' : 'Consolation Round 1';
      const consolationRound1Matches = [];

      for (let i = 0; i < entrantsWithByes.length; i += 2) {
        const entrantA = entrantsWithByes[i];
        const entrantB = entrantsWithByes[i + 1];

        const match = {
          id: generateMatchId(),
          matchNumber: matchNumber++,
          roundNumber: consolationRound1RoundNumber,
          roundName: consolationRound1Name,
          roundType: 'consolation',
          group: 'consolation',
          teamA: null,
          teamB: null,
          loserOfMatchA: entrantA.sourceMatchId,
          loserOfMatchB: entrantB.sourceMatchId,
          isBye: entrantA.isBye || entrantB.isBye,
          winner: null,
          status: 'pending',
          scheduledAt: null,
          venue: null,
        };

        consolationRound1Matches.push(match);
        matches.push(match);
      }

      rounds.push({
        roundNumber: consolationRound1RoundNumber,
        roundName: consolationRound1Name,
        roundType: 'consolation',
        group: 'consolation',
        matches: consolationRound1Matches,
      });

      // Subsequent consolation rounds are normal winner-advancement, exactly
      // like the main bracket's later rounds, just scoped to this mini-bracket.
      let previousConsolationRoundMatches = consolationRound1Matches;
      for (let round = 2; round <= consolationTotalRounds; round++) {
        const roundMatches = [];
        const matchesInRound = previousConsolationRoundMatches.length / 2;
        const roundName = round === consolationTotalRounds ? 'Consolation Final' : `Consolation Round ${round}`;
        const roundNumber = nextDraftRoundNumber++;

        for (let i = 0; i < matchesInRound; i++) {
          const match = {
            id: generateMatchId(),
            matchNumber: matchNumber++,
            roundNumber,
            roundName,
            roundType: 'consolation',
            group: 'consolation',
            teamA: null,
            teamB: null,
            previousMatchA: previousConsolationRoundMatches[i * 2]?.id,
            previousMatchB: previousConsolationRoundMatches[i * 2 + 1]?.id,
            winner: null,
            status: 'pending',
            scheduledAt: null,
            venue: null,
          };

          roundMatches.push(match);
          matches.push(match);
        }

        rounds.push({
          roundNumber,
          roundName,
          roundType: 'consolation',
          group: 'consolation',
          matches: roundMatches,
        });

        previousConsolationRoundMatches = roundMatches;
      }
    }
  }

  return {
    format: 'single_elimination',
    formatName: 'Single Elimination',
    totalRounds,
    bracketSize,
    byesNeeded,
    rounds,
    matches,
    participants: teams,
  };
};

/**
 * DOUBLE ELIMINATION GENERATOR
 * Creates winners and losers brackets with grand final
 */
export const generateDoubleElimination = (participants, options = {}) => {
  const { seeded = false, seedOrder = null } = options;

  let teams = seeded && seedOrder ? seedOrder : shuffleArray([...participants]);

  const bracketSize = nextPowerOf2(teams.length);
  const byesNeeded = bracketSize - teams.length;
  const winnersRounds = Math.log2(bracketSize);
  const losersRounds = (winnersRounds - 1) * 2; // Losers bracket has more rounds

  const rounds = [];
  const matches = [];
  let matchNumber = 1;

  // === WINNERS BRACKET ===
  const teamsWithByes = [...teams];
  for (let i = 0; i < byesNeeded; i++) {
    teamsWithByes.splice(i * 2 + 1, 0, { id: `bye-${i}`, name: 'BYE', isBye: true });
  }

  // Winners Round 1
  const winnersR1Matches = [];
  for (let i = 0; i < teamsWithByes.length; i += 2) {
    const teamA = teamsWithByes[i];
    const teamB = teamsWithByes[i + 1];

    const match = {
      id: generateMatchId(),
      matchNumber: matchNumber++,
      roundNumber: 1,
      roundName: 'Winners Round 1',
      bracket: 'winners',
      teamA,
      teamB,
      isBye: teamA.isBye || teamB.isBye,
      winner: teamA.isBye ? teamB : (teamB.isBye ? teamA : null),
      loser: null,
      status: teamA.isBye || teamB.isBye ? 'completed' : 'pending',
      scheduledAt: null,
      venue: null,
    };

    winnersR1Matches.push(match);
    matches.push(match);
  }

  rounds.push({
    roundNumber: 1,
    roundName: 'Winners Round 1',
    bracket: 'winners',
    roundType: 'elimination',
    matches: winnersR1Matches,
  });

  // Subsequent Winners Rounds
  let prevWinnersMatches = winnersR1Matches;
  for (let round = 2; round <= winnersRounds; round++) {
    const roundMatches = [];
    const matchesInRound = prevWinnersMatches.length / 2;
    const roundName = round === winnersRounds ? 'Winners Final' : `Winners Round ${round}`;

    for (let i = 0; i < matchesInRound; i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: round,
        roundName,
        bracket: 'winners',
        teamA: null,
        teamB: null,
        previousMatchA: prevWinnersMatches[i * 2]?.id,
        previousMatchB: prevWinnersMatches[i * 2 + 1]?.id,
        winner: null,
        loser: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };

      roundMatches.push(match);
      matches.push(match);
    }

    rounds.push({
      roundNumber: round,
      roundName,
      bracket: 'winners',
      roundType: 'elimination',
      matches: roundMatches,
    });

    prevWinnersMatches = roundMatches;
  }

  // === LOSERS BRACKET ===
  // Losers rounds are created as placeholders - losers drop from winners bracket
  let losersMatchCount = bracketSize / 2;
  for (let round = 1; round <= losersRounds; round++) {
    const isDropRound = round % 2 === 1; // Odd rounds receive losers from winners
    const roundName = round === losersRounds ? 'Losers Final' : `Losers Round ${round}`;
    const roundMatches = [];

    const matchesInRound = isDropRound ? losersMatchCount : losersMatchCount / 2;
    if (!isDropRound) losersMatchCount = matchesInRound;

    for (let i = 0; i < Math.max(1, matchesInRound); i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: round,
        roundName,
        bracket: 'losers',
        teamA: null,
        teamB: null,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };

      roundMatches.push(match);
      matches.push(match);
    }

    rounds.push({
      roundNumber: winnersRounds + round,
      roundName,
      bracket: 'losers',
      roundType: 'elimination',
      matches: roundMatches,
    });
  }

  // === GRAND FINAL ===
  const grandFinal = {
    id: generateMatchId(),
    matchNumber: matchNumber++,
    roundNumber: winnersRounds + losersRounds + 1,
    roundName: 'Grand Final',
    bracket: 'final',
    teamA: null, // Winners bracket champion
    teamB: null, // Losers bracket champion
    winner: null,
    status: 'pending',
    scheduledAt: null,
    venue: null,
    note: 'Losers bracket winner must win twice',
  };

  matches.push(grandFinal);
  rounds.push({
    roundNumber: winnersRounds + losersRounds + 1,
    roundName: 'Grand Final',
    bracket: 'final',
    roundType: 'grand_final',
    matches: [grandFinal],
  });

  return {
    format: 'double_elimination',
    formatName: 'Double Elimination',
    winnersRounds,
    losersRounds,
    bracketSize,
    byesNeeded,
    rounds,
    matches,
    participants: teams,
  };
};

/**
 * Reorders matches *within* each round (pairings themselves are untouched —
 * round order and who-plays-who never change, only which match goes
 * first/last within a round) to minimize how often the team that closed one
 * round immediately opens the next.
 *
 * DP over "which match is last in round i" as the state: scores every
 * reachable (first, last) choice per round against every predecessor state,
 * so it finds the true minimum achievable total conflicts across the whole
 * schedule — a one-step-lookback swap can lock in a bad ordering for an
 * earlier round and never revisit it, missing fixes only reachable by
 * reordering that earlier round instead.
 *
 * With an odd participant count only 1 team byes per round, so most teams
 * unavoidably play in both of any two consecutive rounds — this can't
 * eliminate that, it only minimizes stacking a team's rounds edge-to-edge
 * wherever a reordering is actually available.
 *
 * @param {Array<Array<{teamA, teamB}>>} rounds - one array of {teamA, teamB}
 *   pairs per round, in play order. Mutated in place (and returned).
 */
const avoidBackToBackAcrossRounds = (rounds) => {
  if (rounds.length === 0) return rounds;

  const conflictKeys = (pair) => [
    `${pair.teamA.type}-${pair.teamA.id}`,
    `${pair.teamB.type}-${pair.teamB.id}`
  ];
  const conflicts = (a, b) => {
    const idsB = new Set(conflictKeys(b));
    return conflictKeys(a).some(id => idsB.has(id));
  };

  // dpHistory[i] = Map<lastIdx, { cost, firstIndex, prevLastIndex }> — the
  // minimum total conflicts through round i when round i's pair at lastIdx
  // is placed last (first/middle chosen optimally given that constraint).
  const dpHistory = [];
  const dp0 = new Map();
  for (let idx = 0; idx < rounds[0].length; idx++) {
    dp0.set(idx, { cost: 0, firstIndex: null, prevLastIndex: null });
  }
  dpHistory.push(dp0);

  for (let i = 1; i < rounds.length; i++) {
    const prevRound = rounds[i - 1];
    const currRound = rounds[i];
    const prevDp = dpHistory[i - 1];
    const newDp = new Map();

    for (let lastIdx = 0; lastIdx < currRound.length; lastIdx++) {
      let best = null;
      for (let firstIdx = 0; firstIdx < currRound.length; firstIdx++) {
        if (currRound.length > 1 && firstIdx === lastIdx) continue; // first != last unless only 1 match
        for (const [prevLastIdx, prevState] of prevDp.entries()) {
          const cost = prevState.cost + (conflicts(currRound[firstIdx], prevRound[prevLastIdx]) ? 1 : 0);
          if (!best || cost < best.cost) {
            best = { cost, firstIndex: firstIdx, prevLastIndex: prevLastIdx };
          }
        }
      }
      if (best) newDp.set(lastIdx, best);
    }
    dpHistory.push(newDp);
  }

  const finalDp = dpHistory[dpHistory.length - 1];
  let bestFinal = null;
  for (const [lastIdx, state] of finalDp.entries()) {
    if (!bestFinal || state.cost < bestFinal.cost) bestFinal = { lastIdx, ...state };
  }

  const chosen = new Array(rounds.length);
  let currLastIdx = bestFinal.lastIdx;
  for (let i = rounds.length - 1; i >= 0; i--) {
    const state = dpHistory[i].get(currLastIdx);
    chosen[i] = { firstIdx: state.firstIndex, lastIdx: currLastIdx };
    currLastIdx = state.prevLastIndex;
  }

  for (let i = 0; i < rounds.length; i++) {
    const round = rounds[i];
    if (round.length <= 1) continue;
    let { firstIdx, lastIdx } = chosen[i];
    if (firstIdx === null) firstIdx = (lastIdx === 0) ? 1 : 0;
    const reordered = [round[firstIdx]];
    for (let k = 0; k < round.length; k++) {
      if (k !== firstIdx && k !== lastIdx) reordered.push(round[k]);
    }
    if (firstIdx !== lastIdx) reordered.push(round[lastIdx]);
    round.length = 0;
    round.push(...reordered);
  }

  return rounds;
};

/**
 * ROUND ROBIN GENERATOR
 * Each team plays every other team (circle method for scheduling)
 */
export const generateRoundRobin = (participants, options = {}) => {
  const { matchesPerPairing = 1, shuffleTeams = true } = options;

  let teams = shuffleTeams ? shuffleArray([...participants]) : [...participants];

  // Add dummy team if odd number
  const hasBye = teams.length % 2 !== 0;
  if (hasBye) {
    teams.push({ id: 'bye', name: 'BYE', isBye: true });
  }

  const n = teams.length;
  const totalRounds = n - 1;
  const matchesPerRound = n / 2;

  // 1. Compute every round's real (non-bye) pairings first, one array per round —
  // this is what the preview will actually show and what match_number counts,
  // so a bye slot never "uses up" a number that then gets filtered out.
  // Uses the same index-based circle method as the backend generator (rather
  // than this file's old array-rotation variant) so the preview's pairings —
  // and what avoidBackToBackAcrossRounds can achieve on them — match what
  // "Save Schedule" actually persists via the real backend generator.
  const roundsData = [];
  for (let leg = 0; leg < matchesPerPairing; leg++) {
    for (let round = 0; round < totalRounds; round++) {
      const roundNumber = leg * totalRounds + round + 1;
      const roundName = matchesPerPairing > 1
        ? `Matchday ${roundNumber} (Leg ${leg + 1})`
        : `Matchday ${roundNumber}`;

      const pairs = [];
      for (let m = 0; m < matchesPerRound; m++) {
        const home = m === 0 ? 0 : ((round + m) % (n - 1)) + 1;
        const away = m === 0 ? (round % (n - 1)) + 1 : ((round + n - 1 - m) % (n - 1)) + 1;

        const teamA = teams[home];
        const teamB = teams[away];

        if (teamA.isBye || teamB.isBye) continue; // bye
        pairs.push({ teamA, teamB });
      }

      roundsData.push({ roundNumber, roundName, pairs });
    }
  }

  // 2. Reorder matches within each round to minimize how often the team that
  // closed one round immediately opens the next — the tightest possible
  // back-to-back with zero buffer. This is a DP over "which match is last in
  // round i" (mirrors the backend's avoidBackToBackAcrossRounds), not a
  // one-step-lookback swap: a swap-only pass can lock in a bad ordering for
  // an earlier round and never revisit it, missing fixes that were only
  // reachable by reordering that earlier round instead. See the backend's
  // scheduleGenerator.js for the full write-up.
  avoidBackToBackAcrossRounds(roundsData.map(r => r.pairs));

  // 3. Build the actual match objects in the (possibly reordered) sequence —
  // match_number only increments for real matches, so no gaps from byes.
  const rounds = [];
  const matches = [];
  let matchNumber = 1;

  for (const { roundNumber, roundName, pairs } of roundsData) {
    const roundMatches = pairs.map(({ teamA, teamB }) => {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber,
        roundName,
        roundType: 'league',
        teamA,
        teamB,
        isBye: false,
        winner: null,
        teamAScore: null,
        teamBScore: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      matches.push(match);
      return match;
    });

    rounds.push({
      roundNumber,
      roundName,
      roundType: 'league',
      matches: roundMatches,
    });
  }

  // Calculate total matches (excluding byes)
  const actualTeams = hasBye ? teams.length - 1 : teams.length;
  const totalMatches = (actualTeams * (actualTeams - 1) / 2) * matchesPerPairing;

  return {
    format: 'round_robin',
    formatName: 'Round Robin',
    totalRounds: totalRounds * matchesPerPairing,
    matchesPerPairing,
    hasBye,
    rounds,
    matches,
    participants: teams.filter(t => !t.isBye),
    totalMatches,
  };
};

/**
 * LEAGUE + FINALS GENERATOR
 * Round robin league stage followed by knockout finals
 */
export const generateLeaguePlusFinals = (participants, options = {}) => {
  const {
    matchesPerPairing = 1,
    teamsToQualify = Math.min(4, Math.floor(participants.length / 2)),
    finalsFormat = 'single_elimination' // or 'best_of_3'
  } = options;

  let teams = shuffleArray([...participants]);

  // Generate league stage
  const leagueSchedule = generateRoundRobin(teams, { matchesPerPairing, shuffleTeams: false });

  const rounds = [...leagueSchedule.rounds];
  const matches = [...leagueSchedule.matches];
  let matchNumber = matches.length + 1;

  // Add playoff rounds based on teams qualifying
  const playoffSize = nextPowerOf2(teamsToQualify);
  const playoffRounds = Math.log2(playoffSize);

  // Playoff stage
  for (let round = 1; round <= playoffRounds; round++) {
    const matchesInRound = playoffSize / Math.pow(2, round);
    const roundMatches = [];

    let roundName;
    if (round === playoffRounds) roundName = 'Final';
    else if (round === playoffRounds - 1 && matchesInRound === 2) roundName = 'Semi-Finals';
    else if (round === playoffRounds - 2 && matchesInRound === 4) roundName = 'Quarter-Finals';
    else roundName = `Playoff Round ${round}`;

    for (let i = 0; i < matchesInRound; i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: leagueSchedule.totalRounds + round,
        roundName,
        roundType: 'playoff',
        teamA: null, // TBD based on league standings
        teamB: null,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
        playoffSeed: round === 1 ? { seedA: i * 2 + 1, seedB: teamsToQualify - i * 2 } : null,
      };

      roundMatches.push(match);
      matches.push(match);
    }

    rounds.push({
      roundNumber: leagueSchedule.totalRounds + round,
      roundName,
      roundType: 'playoff',
      matches: roundMatches,
    });
  }

  return {
    format: 'league_plus_finals',
    formatName: 'League + Finals',
    leagueRounds: leagueSchedule.totalRounds,
    playoffRounds,
    teamsToQualify,
    rounds,
    matches,
    participants: teams,
    totalMatches: matches.length,
  };
};

/**
 * IPL-STYLE GENERATOR
 * League stage + Qualifier 1 + Eliminator + Qualifier 2 + Final
 */
export const generateIPLStyle = (participants, options = {}) => {
  const {
    leagueMatchesPerTeam = null, // If null, full round robin
    teamsToQualify = 4,
    matchesPerPairing = 2, // Home and away
  } = options;

  let teams = shuffleArray([...participants]);

  if (teams.length < 4) {
    throw new Error('IPL-style format requires at least 4 teams');
  }

  // Generate league stage
  const leagueSchedule = generateRoundRobin(teams, { matchesPerPairing, shuffleTeams: false });

  const rounds = [...leagueSchedule.rounds];
  const matches = [...leagueSchedule.matches];
  let matchNumber = matches.length + 1;
  const leagueRoundCount = leagueSchedule.totalRounds;

  // === PLAYOFF STAGE (IPL Style) ===

  // Qualifier 1: 1st vs 2nd (winner goes to Final)
  const qualifier1 = {
    id: generateMatchId(),
    matchNumber: matchNumber++,
    roundNumber: leagueRoundCount + 1,
    roundName: 'Qualifier 1',
    roundType: 'qualifier',
    teamA: null, // 1st place
    teamB: null, // 2nd place
    winner: null,
    loser: null,
    status: 'pending',
    scheduledAt: null,
    venue: null,
    description: 'Winner advances to Final, Loser plays Qualifier 2',
    seedA: 1,
    seedB: 2,
  };
  matches.push(qualifier1);

  rounds.push({
    roundNumber: leagueRoundCount + 1,
    roundName: 'Qualifier 1',
    roundType: 'qualifier',
    matches: [qualifier1],
  });

  // Eliminator: 3rd vs 4th (loser eliminated)
  const eliminator = {
    id: generateMatchId(),
    matchNumber: matchNumber++,
    roundNumber: leagueRoundCount + 2,
    roundName: 'Eliminator',
    roundType: 'eliminator',
    teamA: null, // 3rd place
    teamB: null, // 4th place
    winner: null,
    status: 'pending',
    scheduledAt: null,
    venue: null,
    description: 'Winner plays Qualifier 2, Loser is eliminated',
    seedA: 3,
    seedB: 4,
  };
  matches.push(eliminator);

  rounds.push({
    roundNumber: leagueRoundCount + 2,
    roundName: 'Eliminator',
    roundType: 'eliminator',
    matches: [eliminator],
  });

  // Qualifier 2: Loser of Q1 vs Winner of Eliminator
  const qualifier2 = {
    id: generateMatchId(),
    matchNumber: matchNumber++,
    roundNumber: leagueRoundCount + 3,
    roundName: 'Qualifier 2',
    roundType: 'qualifier',
    teamA: null, // Loser of Qualifier 1
    teamB: null, // Winner of Eliminator
    winner: null,
    status: 'pending',
    scheduledAt: null,
    venue: null,
    description: 'Winner advances to Final',
    previousMatchA: qualifier1.id,
    previousMatchB: eliminator.id,
  };
  matches.push(qualifier2);

  rounds.push({
    roundNumber: leagueRoundCount + 3,
    roundName: 'Qualifier 2',
    roundType: 'qualifier',
    matches: [qualifier2],
  });

  // Final: Winner of Q1 vs Winner of Q2
  const final = {
    id: generateMatchId(),
    matchNumber: matchNumber++,
    roundNumber: leagueRoundCount + 4,
    roundName: 'Final',
    roundType: 'final',
    teamA: null, // Winner of Qualifier 1
    teamB: null, // Winner of Qualifier 2
    winner: null,
    status: 'pending',
    scheduledAt: null,
    venue: null,
    description: 'Championship Match',
    previousMatchA: qualifier1.id,
    previousMatchB: qualifier2.id,
  };
  matches.push(final);

  rounds.push({
    roundNumber: leagueRoundCount + 4,
    roundName: 'Final',
    roundType: 'final',
    matches: [final],
  });

  return {
    format: 'ipl_style',
    formatName: 'IPL-Style (League + Playoffs)',
    leagueRounds: leagueRoundCount,
    playoffRounds: 4,
    teamsToQualify,
    rounds,
    matches,
    participants: teams,
    totalMatches: matches.length,
    playoffStructure: {
      qualifier1: qualifier1.id,
      eliminator: eliminator.id,
      qualifier2: qualifier2.id,
      final: final.id,
    },
  };
};

/**
 * SWISS SYSTEM GENERATOR (Bonus format)
 * Teams with similar records play each other
 */
export const generateSwissSystem = (participants, options = {}) => {
  const { totalRounds = Math.ceil(Math.log2(participants.length)) + 1 } = options;

  let teams = shuffleArray([...participants]);

  // Add bye if odd
  const hasBye = teams.length % 2 !== 0;
  if (hasBye) {
    teams.push({ id: 'bye', name: 'BYE', isBye: true });
  }

  const rounds = [];
  const matches = [];
  let matchNumber = 1;

  // First round: random pairing
  const round1Matches = [];
  for (let i = 0; i < teams.length; i += 2) {
    const teamA = teams[i];
    const teamB = teams[i + 1];
    const isByeMatch = teamA.isBye || teamB.isBye;

    const match = {
      id: generateMatchId(),
      matchNumber: matchNumber++,
      roundNumber: 1,
      roundName: 'Swiss Round 1',
      roundType: 'swiss',
      teamA,
      teamB,
      isBye: isByeMatch,
      winner: null,
      status: isByeMatch ? 'bye' : 'pending',
      scheduledAt: null,
      venue: null,
    };

    if (!isByeMatch) {
      round1Matches.push(match);
      matches.push(match);
    }
  }

  rounds.push({
    roundNumber: 1,
    roundName: 'Swiss Round 1',
    roundType: 'swiss',
    matches: round1Matches,
  });

  // Subsequent rounds are TBD based on results
  for (let round = 2; round <= totalRounds; round++) {
    rounds.push({
      roundNumber: round,
      roundName: `Swiss Round ${round}`,
      roundType: 'swiss',
      matches: [], // To be generated after previous round results
      note: 'Pairings will be determined based on current standings',
    });
  }

  return {
    format: 'swiss_system',
    formatName: 'Swiss System',
    totalRounds,
    hasBye,
    rounds,
    matches,
    participants: teams.filter(t => !t.isBye),
    totalMatches: (teams.filter(t => !t.isBye).length / 2) * totalRounds,
  };
};

/**
 * GROUP + KNOCKOUT GENERATOR (client-side preview only)
 * Splits teams into pools that play round robin, then a knockout bracket
 * whose slots stay TBD — the backend seeds them from group standings once
 * pool play completes (see populateKnockoutFromGroups server-side), so this
 * preview can't and doesn't predict who actually reaches the bracket.
 */
export const generateGroupKnockout = (participants, options = {}) => {
  const { numberOfGroups = 2, advancePerGroup = 2 } = options;
  const groupCount = Math.max(2, numberOfGroups);

  if (participants.length < groupCount * 2) {
    throw new Error(`Group + Knockout needs at least ${groupCount * 2} teams for ${groupCount} groups`);
  }

  const groupLabels = 'ABCDEFGH'.split('');
  const shuffled = shuffleArray([...participants]);
  const teamsPerGroup = Math.ceil(shuffled.length / groupCount);

  const rounds = [];
  const matches = [];
  let matchNumber = 1;
  let groupStageRounds = 0;

  for (let g = 0; g < groupCount; g++) {
    const groupName = groupLabels[g];
    const groupTeams = shuffled.slice(g * teamsPerGroup, (g + 1) * teamsPerGroup);
    if (groupTeams.length < 2) continue;

    const groupSchedule = generateRoundRobin(groupTeams, { shuffleTeams: false });
    groupStageRounds = Math.max(groupStageRounds, groupSchedule.totalRounds);

    groupSchedule.rounds.forEach(round => {
      const roundName = `Group ${groupName} - ${round.roundName}`;
      const groupMatches = round.matches.map(m => ({
        ...m,
        matchNumber: matchNumber++,
        roundName,
        roundType: 'round_robin',
        groupName,
      }));
      groupMatches.forEach(m => matches.push(m));
      rounds.push({
        roundNumber: round.roundNumber,
        roundName,
        roundType: 'round_robin',
        groupName,
        matches: groupMatches,
      });
    });
  }

  // Knockout stage — every slot is TBD in this preview; the backend only
  // fills participant_a/b once each group's round robin finishes.
  const knockoutParticipants = groupCount * advancePerGroup;
  const knockoutBracketSize = nextPowerOf2(knockoutParticipants);
  const knockoutRounds = Math.log2(knockoutBracketSize);

  let previousRoundMatches = [];
  for (let round = 1; round <= knockoutRounds; round++) {
    const matchesInRound = knockoutBracketSize / Math.pow(2, round);
    const roundName = getEliminationRoundName(round, knockoutRounds);
    const roundMatches = [];

    for (let i = 0; i < matchesInRound; i++) {
      const match = {
        id: generateMatchId(),
        matchNumber: matchNumber++,
        roundNumber: groupStageRounds + round,
        roundName,
        roundType: 'knockout',
        teamA: null,
        teamB: null,
        previousMatchA: previousRoundMatches[i * 2]?.id,
        previousMatchB: previousRoundMatches[i * 2 + 1]?.id,
        winner: null,
        status: 'pending',
        scheduledAt: null,
        venue: null,
      };
      roundMatches.push(match);
      matches.push(match);
    }

    rounds.push({
      roundNumber: groupStageRounds + round,
      roundName,
      roundType: 'knockout',
      matches: roundMatches,
    });

    previousRoundMatches = roundMatches;
  }

  return {
    format: 'group_knockout',
    formatName: 'Pool Play + Bracket',
    groups: groupCount,
    advancePerGroup,
    groupStageRounds,
    knockoutRounds,
    rounds,
    matches,
    participants: shuffled,
    totalMatches: matches.length,
  };
};

/**
 * Main generator function - routes to specific format
 */
export const generateTournamentSchedule = (participants, format, options = {}) => {
  if (!participants || participants.length < 2) {
    throw new Error('At least 2 participants are required');
  }

  switch (format) {
    case 'single_elimination':
      return generateSingleElimination(participants, options);
    case 'double_elimination':
      return generateDoubleElimination(participants, options);
    case 'round_robin':
      return generateRoundRobin(participants, options);
    case 'league_plus_finals':
      return generateLeaguePlusFinals(participants, options);
    case 'ipl_style':
      return generateIPLStyle(participants, options);
    case 'swiss_system':
      return generateSwissSystem(participants, options);
    case 'group_knockout':
      return generateGroupKnockout(participants, options);
    default:
      throw new Error(`Unknown tournament format: ${format}`);
  }
};

/**
 * Format descriptions for UI
 */
export const TOURNAMENT_FORMATS = [
  {
    id: 'single_elimination',
    name: 'Single Elimination',
    shortName: 'Knockout',
    description: 'Lose once and you\'re out. Quick and decisive tournament format.',
    minTeams: 2,
    icon: 'trophy',
    recommendedFor: 'Quick tournaments, playoffs',
  },
  {
    id: 'double_elimination',
    name: 'Double Elimination',
    shortName: 'Double KO',
    description: 'Teams must lose twice to be eliminated. Includes winners and losers brackets.',
    minTeams: 4,
    icon: 'shield',
    recommendedFor: 'Competitive tournaments where one bad game shouldn\'t end your run',
  },
  {
    id: 'round_robin',
    name: 'Round Robin',
    shortName: 'League',
    description: 'Everyone plays everyone. Best for determining overall strength.',
    minTeams: 3,
    icon: 'refresh-cw',
    recommendedFor: 'Leagues, fair ranking of all teams',
  },
  {
    id: 'league_plus_finals',
    name: 'League + Finals',
    shortName: 'League + KO',
    description: 'Round robin league stage followed by knockout playoffs for top teams.',
    minTeams: 4,
    icon: 'award',
    recommendedFor: 'Full season tournaments with exciting playoffs',
  },
  {
    id: 'ipl_style',
    name: 'IPL-Style Playoffs',
    shortName: 'IPL Format',
    description: 'League stage + Qualifier 1, Eliminator, Qualifier 2, and Final. Top 2 teams get two chances.',
    minTeams: 4,
    icon: 'zap',
    recommendedFor: 'Professional leagues, exciting playoff drama',
  },
  {
    id: 'swiss_system',
    name: 'Swiss System',
    shortName: 'Swiss',
    description: 'Teams with similar records play each other. Efficient for large fields.',
    minTeams: 4,
    icon: 'git-branch',
    recommendedFor: 'Large tournaments, chess-style competitions',
    comingSoon: true,
  },
  {
    id: 'group_knockout',
    name: 'Pool Play + Bracket',
    shortName: 'Pool + Bracket',
    description: 'Teams are split into pools for round-robin play, then top finishers advance to a knockout bracket.',
    minTeams: 4,
    icon: 'users2',
    recommendedFor: 'Larger fields that want guaranteed pool games before a decisive bracket',
  },
];

export default generateTournamentSchedule;
